// mission-initializer-provenance.ts
//
// Tách THUẦN từ mission-initializer.ts (phase 10, lô A): 3 bước ghi provenance
// (actor binding, declared resources, blueprint binding) mà `initMission` chạy
// liên tiếp sau khi tạo mission workspace (bước 6b/6c/6d gốc). Gộp vào 1 hàm
// orchestration để `initMission` gọi 1 lần — không đổi thứ tự/side effect.

import { writeMissionActorBinding } from "./mission-actor-binding.js";
import { writeMissionBlueprintBinding } from "./mission-blueprint-binding.js";
import { writeMissionDeclaredResources } from "./mission-declared-resources.js";
import { NULL_OID } from "./mission-initializer-preflight.js";
import type { InitRequest } from "./schemas/init-schema.js";

export interface PersistInitProvenanceInput {
  controlRoot: string;
  request: InitRequest;
  actorOsAccount: string;
  boundMattermostUserId: string | undefined;
  now: () => Date;
}

/**
 * 6b. Persist the bound actor identity (Bug J, P15): without a stored
 * identity, `approveGate`'s actor-binding check would have to trust
 * the directive's own actor_id — self-authorisation. Written BEFORE
 * gates are requested so a binding always exists for any approval.
 * P19b: also bind the owner's Mattermost identity — the two directive
 * producers use different actor_id namespaces (CLI $USER vs. chat
 * user id), so `os_account` alone can never match a chat-filed
 * approval. Source is `mattermost.approvers` (operator-owned config),
 * NEVER worker output; absent approvers => chat path fails closed.
 *
 * 6c. Persist the declared resource boundary (P25, spec §8 condition
 * 4). Same Bug J shape as 6b above: `resource_names` was validated by
 * InitRequestSchema and then dropped, leaving the G2 decision nothing
 * to measure "đụng ranh giới scope" against. Written from the VALIDATED
 * request — the person's declaration at init, never worker output, so
 * the party the gate governs cannot write the boundary it is judged by.
 *
 * 6d. B3: persist WHICH file G0's hash belongs to, so the dispatch
 * guard can re-hash it. Same provenance rule as 6b/6c (validated request).
 */
export async function persistInitProvenance(input: PersistInitProvenanceInput): Promise<void> {
  const { controlRoot, request, actorOsAccount, boundMattermostUserId, now } = input;

  await writeMissionActorBinding(controlRoot, {
    os_account: actorOsAccount,
    ...(boundMattermostUserId ? { mattermost_user_id: boundMattermostUserId } : {}),
    bound_at: now().toISOString(),
  });

  await writeMissionDeclaredResources(controlRoot, request.resource_names, now);

  await writeMissionBlueprintBinding(
    controlRoot,
    {
      source_path: request.blueprint.source_path,
      bytes_sha256: request.blueprint.bytes_sha256,
      // H20: preflight verified the bytes at base_sha unless base is null-oid.
      verified_at_base_sha: request.repo.base_sha.toLowerCase() !== NULL_OID,
    },
    now,
  );
}
